import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { useMgmtBrand } from "@/lib/useMgmtBrand";

/**
 * Reusable branded reset-password page for the Worker and Management
 * USA NJ portals. Reads `?token=...` from the URL (matching the link
 * embedded in the branded forgot-password email) and calls the
 * portal-scoped reset endpoint.
 */
export default function BrandedResetPassword({
  brand, // { name, tagline, apiPrefix, loginPath }
  testidPrefix,
}) {
  useMgmtBrand(`${brand.name} — Reset password`);
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [needsBusiness, setNeedsBusiness] = useState(false);
  const [bizName, setBizName] = useState("");
  const [bizEmail, setBizEmail] = useState("");

  useEffect(() => {
    if (!token || brand.apiPrefix !== "/mgmt") return;
    api.get(`${brand.apiPrefix}/auth/reset-info`, { params: { token } })
      .then((r) => setNeedsBusiness(!!r.data?.needs_business))
      .catch(() => {});
  }, [token, brand.apiPrefix]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("This reset link is missing its token — please request a new one.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    if (needsBusiness && (!bizName.trim() || !bizEmail.trim())) {
      return toast.error("Please enter your business name and business email.");
    }
    setLoading(true);
    try {
      const payload = { token, password };
      if (needsBusiness) {
        payload.business_name = bizName.trim();
        payload.business_email = bizEmail.trim();
      }
      await api.post(`${brand.apiPrefix}/auth/reset-password`, payload);
      setDone(true);
      setTimeout(() => navigate(brand.loginPath, { replace: true }), 2500);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-black tracking-tighter" data-testid={`${testidPrefix}-title`}>
            {brand.name}
          </h1>
          <p className="text-sm text-slate-400 mt-1">{brand.tagline}</p>
        </div>

        <div className="bg-slate-800/60 border border-slate-700 rounded-md p-6 shadow-xl">
          {done ? (
            <div className="text-center space-y-3" data-testid={`${testidPrefix}-done`}>
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              </div>
              <h2 className="font-semibold text-lg">Password updated</h2>
              <p className="text-sm text-slate-300">Taking you to the sign-in page…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
              <div>
                <h2 className="font-semibold text-lg">Set a new password</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Pick a fresh password. Minimum 6 characters.
                </p>
              </div>
              {needsBusiness && (
                <>
                  <div>
                    <Label htmlFor="rbn" className="text-slate-300">Business name</Label>
                    <input
                      id="rbn"
                      value={bizName}
                      onChange={(e) => setBizName(e.target.value)}
                      required
                      placeholder="Your business name"
                      className="mt-1 w-full h-10 rounded-md px-3 text-sm bg-slate-900 border border-slate-700 text-slate-50 outline-none focus:border-emerald-500"
                      data-testid={`${testidPrefix}-business-name`}
                    />
                  </div>
                  <div>
                    <Label htmlFor="rbe" className="text-slate-300">Business email</Label>
                    <input
                      id="rbe"
                      type="email"
                      value={bizEmail}
                      onChange={(e) => setBizEmail(e.target.value)}
                      required
                      placeholder="you@yourbusiness.com"
                      className="mt-1 w-full h-10 rounded-md px-3 text-sm bg-slate-900 border border-slate-700 text-slate-50 outline-none focus:border-emerald-500"
                      data-testid={`${testidPrefix}-business-email`}
                    />
                    <p className="text-xs text-slate-500 mt-1">Documents you send for signing will use this name & reply-to email.</p>
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="rp1" className="text-slate-300">New password</Label>
                <PasswordInput
                  id="rp1"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  className="bg-slate-900 border-slate-700 text-slate-50"
                  data-testid={`${testidPrefix}-password`}
                />
              </div>
              <div>
                <Label htmlFor="rp2" className="text-slate-300">Confirm password</Label>
                <PasswordInput
                  id="rp2"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="bg-slate-900 border-slate-700 text-slate-50"
                  data-testid={`${testidPrefix}-confirm`}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid={`${testidPrefix}-submit`}
              >
                {loading ? "Saving…" : "Save new password"}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to={brand.loginPath}
                  className="text-slate-400 hover:text-slate-200"
                  data-testid={`${testidPrefix}-back-login`}
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
